<template>
	<Modal @requestUnmount="$emit('safeToUnmount')" width="2xl">
		<template #header>
			<div class="font-semibold text-lg mb-6">{{ $t("Share this collection") }}</div>
			<p class="setting-explanation ml-4 mb-4">
				{{ $t("Lesson plans in a shared collection are visible and usable by the audience that you set below.") }}
				<button class="btn-help ml-1" style="display: inline-flex; vertical-align: middle;" @click="showShareInfo = !showShareInfo"></button>
			</p>
		</template>
		<template #default="modalActions">
			<div class="font-semibold ml-4">{{ $t("Share with") }}...</div>

			<!-- options nobody, few users, school group and everyone -->
			<div class="flex flex-col gap-4 mt-2 mb-4 ml-4">
				<!-- private -->
				<label for="share-with-nobody" class="share-option cursor-pointer">
					<div>
						<div class="flex gap-2 items-start">
							<NoSymbolIcon class="w-5 h-5 shrink-0" />
							<div>
								<p class="font-semibold">
									{{ $t("Nobody") }}
								</p>
								<p class="text-base-content-light">{{ $t("Don’t share this collection of lesson plans") }}</p>
							</div>
						</div>
					</div>
					<input type="radio" name="radio" id="share-with-nobody" class="radio" v-model="selectedSharing" :value="null" />
				</label>

				<!-- account -->
				<label for="share-with-account" class="share-option cursor-pointer">
					<div>
						<div class="flex gap-2 items-start">
							<UsersOutlineIcon class="w-5 h-5 shrink-0" />
							<div>
								<p class="font-semibold">
									{{ $t("Your direct colleagues") }}
								</p>
								<p class="text-base-content-light">{{ $t("Share this collection with colleagues in the Smart Symbols school account(s) you are a member of") }}</p>
							</div>
						</div>
					</div>
					<input type="radio" name="radio" id="share-with-account" class="radio" v-model="selectedSharing" :value="'account'" />
				</label>

				<!-- school group -->
				<label for="share-with-group" class="share-option" :class="schoolGroups.length ? 'cursor-pointer' : 'cursor-not-allowed'">
					<div>
						<div class="flex gap-2 items-start" :class="schoolGroups.length ? '' : 'text-base-content-light'">
							<UsersIcon class="w-5 h-5 shrink-0" />
							<div>
								<p class="font-semibold">{{ groupOptionTitle }}</p>
								<p class="text-base-content-light">{{ $t("Share this collection with all Smart Symbols colleagues in your school group") }}</p>
								<!-- no school group: the option is disabled, explain why -->
								<p v-if="!schoolGroups.length" class="text-base-content-light italic text-sm pt-2">
									{{ $t("Your school is not part of a school group") }}
								</p>
								<!-- member of schools in several school groups: let the user pick one -->
								<select
									v-else-if="schoolGroups.length > 1"
									v-model="selectedGroupId"
									class="select select-bordered select-sm mt-2"
									@click.stop
								>
									<option v-for="group in schoolGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
								</select>
							</div>
						</div>
					</div>
					<input type="radio" name="radio" id="share-with-group" class="radio" v-model="selectedSharing" :value="'group'" :disabled="!schoolGroups.length" />
				</label>

				<!-- everyone -->
				<label for="share-with-everyone" class="share-option" :class="me.authorType ? 'cursor-pointer' : 'cursor-not-allowed'">
					<div>
						<div class="flex gap-2 items-start" :class="me?.authorType ? '' : 'text-base-content-light'">
							<GlobeEuropeAfricaIcon class="w-5 h-5 shrink-0" />
							<div>
								<p class="font-semibold">
									{{ $t("Everyone") }}
								</p>
								<p class="text-base-content-light">{{ $t("Share with all Smart Symbols users") }}</p>
								<p v-if="!me.authorType" class="text-base-content-light italic text-sm pt-2">
									{{ $t("#instruction: public collection sharing", {contact: "authors@smartsymbols.com"}) }}
								</p>
							</div>
						</div>
					</div>
					<input type="radio" name="radio" id="share-with-everyone" class="radio" v-model="selectedSharing" :value="'everyone'" :disabled="!me.authorType" />
				</label>
			</div>

			<div class="modal-footer flex justify-end gap-1 overflow-hidden">
				<button class="btn btn-ghost" @click="modalActions.hide">{{ $t("Cancel") }}</button>
				<button class="btn btn-primary" @click="confirmShare(modalActions)" :disabled="saving">
					<span v-show="saving" class="loading loading-spinner"></span>
					{{ $t("Save") }}
				</button>
			</div>
		</template>
	</Modal>

	<KBView v-if="showShareInfo" :url="{
		en: '',
		nl: 'https://handsomely-cicada-2cb.notion.site/ebd//34a0964c114280a4abb6c7ac16cae054',
	}" @requestClose="showShareInfo = false" />

</template>

<style>
.share-option {
	@apply flex justify-between items-start p-2 hover:bg-base-100 transition-colors rounded-lg;
}
</style>


<script lang="ts">
import Modal from "@/components/ui/Modal.vue";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { NoSymbolIcon, UsersIcon, GlobeEuropeAfricaIcon } from "@heroicons/vue/24/solid";
import { UsersIcon as UsersOutlineIcon } from "@heroicons/vue/24/outline";
import { LessonPlanCollection } from '@/models/LessonPlanCollection.model';
import { useUsersStore } from "@/stores/Users.store";
import { mapStores, mapState } from "pinia";
import KBView from "@/components/KBView.vue";

export default {
	name: "LessonPlanCollectionShareModal",

	components: {
		NoSymbolIcon,
		UsersIcon,
		UsersOutlineIcon,
		GlobeEuropeAfricaIcon,
		Modal,
		KBView,
	},

	props: {
		collection: {
			type: LessonPlanCollection,
			required: true,
		},
	},

	emits: ['safeToUnmount', 'sharingChanged'],

	data() {
		return {
			selectedSharing: (this.collection?.sharing ?? null) as null | 'account' | 'group' | 'everyone',
			selectedGroupId: (this.collection?.sharingWithSubjectId ?? null) as string | null,
			showShareInfo: false,
			saving: false,
			editableModel: null as LessonPlanCollection | null,
		};
	},

	beforeMount() {
		this.editableModel = this.collection.clone()

		// If the group we were shared with is no longer among the user's
		// current school groups (e.g. membership moved), fall back to the
		// first current group so the UI and the persisted state agree.
		// If the user currently has no groups, leave the stored subject
		// untouched: the group option is disabled, and preserving it lets
		// an already-group-shared collection keep its share on save.
		if (this.schoolGroups.length && !this.schoolGroups.find(g => g.id === this.selectedGroupId)) {
			this.selectedGroupId = this.schoolGroups[0].id;
		}
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useUsersStore),
		...mapState(useUsersStore, ['me']),

		schoolGroups(): { id: string, name: string }[] {
			return this.me?.getSchoolGroups() ?? [];
		},

		// Names the group when there is exactly one; otherwise (none, or several
		// to pick from in the select below) falls back to the generic label.
		groupOptionTitle(): string {
			if (this.schoolGroups.length === 1) {
				return this.$t("All colleagues in {group}", { group: this.schoolGroups[0].name });
			}
			return this.$t("All colleagues in your school group");
		},
	},

	watch: {
		selectedSharing(value: string | null) {
			if (value === 'group' && !this.selectedGroupId && this.schoolGroups.length) {
				this.selectedGroupId = this.schoolGroups[0].id;
			}
		},
	},

	methods: {
		async confirmShare(modalActions: any) {
			try {
				this.saving = true;
				this.editableModel.sharing = this.selectedSharing;
				this.editableModel.sharingWithSubjectId =
					this.selectedSharing === 'group' ? this.selectedGroupId : null;
				await this.lessonPlanCollectionsStore.save(this.editableModel);
				this.$emit('sharingChanged', this.selectedSharing);
				modalActions.hide();
			} catch (e: any) {
				console.error('Error updating collection:', e);
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>
